import { iconWhiteTrash } from "assets/icons";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { CheckBox } from "components/CheckBox";
import { InputFormField } from "components/InputFormField";
import { InputMultipleFiles } from "components/InputMultipleFiles";
import { InputTranscriptFile } from "components/InputTranscriptFile";
import { Modal } from "components/Modal";
import { MultiSelect } from "components/MultiSelect";
import { QuestionnaireUpdateForm } from "components/QuestionnaireUpdateForm";
import { Questionnaire, QuestionsList } from "components/QuestionsList";
import { SelectModified } from "components/SelectModified";
import { TextFormField } from "components/TextFormField";
import { useAlert } from "hooks/useAlert";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Container,
  FormActions,
  FormSection,
  QuestionActions,
  QuestionsListContainer,
  Wrapper,
} from "./styles";
import { useSessionForm } from "./useSessionForm";

interface SessionFormProps {
  eventId?: string;
  sessionId?: string;
}

export const SessionForm: React.FC<SessionFormProps> = ({
  eventId,
  sessionId,
}) => {
  const { push } = useHistory();
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const { setTextAlertInterface, openAlert, closeAlert, alert } = useAlert();
  const [editingSession, setEditingSession] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isQuestionnaireModalVisible, setIsQuestionnaireModalVisible] =
    useState(false);

  const onSuccess = () => {
    push(`/events/${eventId}?tab=Sessões`);
  };

  const onGenerateQuestionnaire = (questionnaire: Questionnaire) => {
    setEditingSession({
      ...editingSession,
      questionnaire: questionnaire,
    });
  };

  const onQuestionnaireUpdate = (updatedQuestionnaire?: Questionnaire) => {
    setEditingSession({
      ...editingSession,
      questionnaire: updatedQuestionnaire,
    });
    setIsQuestionnaireModalVisible(false);
  };

  const handleOpenQuestionnaireModal = () => {
    setIsQuestionnaireModalVisible(true);
  };

  const handleCloseQuestionnaireModal = () => {
    setIsQuestionnaireModalVisible(false);
  };

  const {
    handleInputChange,
    handleSelectChange,
    inputsValues,
    touched,
    errors,
    handleSubmit,
    isLoading,
    isEditing,
    tracks,
    speakers,
    locations,
    loadingOptions,
    handleTracksArrayChange,
    handleSpeakersArrayChange,
    handleCreatePublicChannelChange,
    handleAllowPublicViewChange,
    hasExistingConversation,
    checkingConversation,
    // Questionnaire-related
    transcriptFile,
    generatingQuestionnaire,
    publishingQuestionnaire,
    handleFileUpload,
    handleGenerateQuestionnaire,
    handlePublishQuestionnaire,
    // Multiple files
    uploadedFiles,
    handleAddFiles,
    handleRemoveFile,
  } = useSessionForm(
    onSuccess,
    onGenerateQuestionnaire,
    editingSession,
    eventId
  );

  const getErrorMessage = (error: any): string | undefined => {
    if (typeof error === "string") return error;
    if (Array.isArray(error)) return error[0];
    return undefined;
  };

  // Load session data for editing
  useEffect(() => {
    if (sessionId) {
      loadSessionData();
    }
  }, [sessionId]);

  // Handle alert confirmation for delete
  useEffect(() => {
    if (alert.typeContinue === "save") {
      executeDeleteSession();
    }
  }, [alert]);

  const loadSessionData = async () => {
    if (!sessionId) return;

    setLoading(true);

    try {
      const query = `
                query BackofficeSession($sessionId: ID!) {
									backofficeSession(sessionId: $sessionId) {
										sessionId
										eventId
										title
										description
										startTime
										durationMinutes
										location{
										locationId
                            name
                        }
										type
										numberOfSeats
										availableSeats
										insertDate
										updateDate
										videoLink
										tracks {
												trackId
												name
										}
										speakers {
												registrationId
												profile {
														name
												}
										}
										transcript
										presentationFiles {
											fileId
											filename
											fileUrl
											signedFileUrl
											mimeType
											originalFilename
										}
										questionnaire {
											questionnaireId
											name
											status
											isPublished
											updateDate
											passingScore
											description
											questions {
												questionId
												questionnaireId
												name
												description
												type
												options {
													id
													text
													isCorrect
													score
													nextQuestionId
												}
												scoreWeight
												allowMultipleAnswers
												status
												nextQuestionId
												insertDate
												updateDate
											}
										}
									}
								}
            `;

      const response = await connectGraphQLApi({
        query,
        variables: { sessionId },
        errorMessage: "Falha ao carregar detalhes da sessão",
        useNewApi: true,
      });

      const sessionDetails = response.data.backofficeSession;

      if (sessionDetails) {
        // Verify that this session belongs to the current event
        if (sessionDetails.eventId !== eventId) {
          setToast({
            type: "error",
            message: "Sessão não pertence a este evento",
            isActive: true,
            timeToRemoveToast: 3000,
          });
          push(`/events/${eventId}`);
          return;
        }

        setEditingSession(sessionDetails);
      } else {
        setToast({
          type: "error",
          message: "Sessão não encontrada",
          isActive: true,
          timeToRemoveToast: 3000,
        });
        push(`/events/${eventId}`);
      }
    } catch (error) {
      console.error("Error loading session details:", error);
      setToast({
        type: "error",
        message: "Erro ao carregar sessão",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = () => {
    if (!sessionId || !editingSession) return;

    setTextAlertInterface({
      title: "Excluir Sessão?",
      description: `Ao continuar, a sessão "${editingSession.title}" e todas as suas informações serão removidas.\nAtenção! Essa ação não pode ser revertida.`,
      closeAlertText: "Cancelar",
      continueAlertText: "Excluir",
      closeAlertTypeBtn: "outline",
      continueAlertTypeBtn: "danger",
      positionCenter: true,
      actionNeeded: false,
    });

    openAlert(true);
  };

  const executeDeleteSession = async () => {
    if (!editingSession) return;

    try {
      const response = await connectGraphQLApi({
        query: `
                    mutation BackofficeDeleteSession($sessionId: ID!) {
                        backofficeDeleteSession(sessionId: $sessionId)
                    }
                `,
        variables: { sessionId: editingSession.sessionId },
        errorMessage: "Falha ao excluir sessão",
        useNewApi: true,
      });

      if (response.data.backofficeDeleteSession) {
        setToast({
          type: "success",
          message: "Sessão excluída com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        // Redirect back to event details
        push(`/events/${eventId}`);
      } else {
        setToast({
          type: "error",
          message: "Falha ao excluir sessão",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      setToast({
        type: "error",
        message: "Erro ao excluir sessão. Tente novamente.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      closeAlert();
    }
  };

  // Memoize options to prevent recreating arrays on every render
  const trackOptions = useMemo(() => {
    return tracks.map((track) => ({
      value: track.trackId,
      label: track.name,
    }));
  }, [tracks]);

  const speakerOptions = useMemo(() => {
    return speakers.map((speaker) => ({
      value: speaker.registrationId,
      label: speaker.profile?.name || "Nome não disponível",
    }));
  }, [speakers]);

  const locationOptions = useMemo(() => {
    return [
      { value: "", label: "Selecione um local" },
      ...locations.map((location) => ({
        value: location.locationId,
        label: location.name,
      })),
    ];
  }, [locations]);

  // These are now stable callbacks from the hook
  // No need to wrap them again since they're already memoized

  if (loading) {
    return <Container>Carregando...</Container>;
  }

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Card title="Informações da Sessão" typography="subtitle-medium">
          <FormSection>
            <Wrapper>
              <InputFormField
                label="Título *"
                name="title"
                value={inputsValues.title}
                onChange={handleInputChange}
                error={touched.title && !!errors.title}
                mensageError={getErrorMessage(errors.title)}
                block
              />
            </Wrapper>

            <Wrapper>
              <TextFormField
                label="Descrição"
                name="description"
                value={inputsValues.description}
                onChange={handleInputChange}
                error={touched.description && !!errors.description}
                mensageError={getErrorMessage(errors.description)}
                block
                rows={4}
              />
            </Wrapper>

            <Wrapper>
              <InputFormField
                label="Data e Hora de Início *"
                name="startTime"
                type="datetime-local"
                value={inputsValues.startTime}
                onChange={handleInputChange}
                error={touched.startTime && !!errors.startTime}
                mensageError={getErrorMessage(errors.startTime)}
                block
              />
            </Wrapper>

            <Wrapper>
              <InputFormField
                label="Duração (minutos) *"
                name="durationMinutes"
                type="number"
                value={inputsValues.durationMinutes.toString()}
                onChange={handleInputChange}
                error={touched.durationMinutes && !!errors.durationMinutes}
                mensageError={getErrorMessage(errors.durationMinutes)}
                block
              />
            </Wrapper>

            <Wrapper>
              {loadingOptions ? (
                <div>Carregando locais...</div>
              ) : (
                <SelectModified
                  label="Local"
                  name="location"
                  value={inputsValues.location}
                  onChange={handleSelectChange}
                  error={touched.location && !!errors.location}
                  errorMessage={getErrorMessage(errors.location)}
                  options={locationOptions}
                  block
                />
              )}
            </Wrapper>

            <Wrapper>
              <SelectModified
                label="Tipo de Sessão"
                name="type"
                value={inputsValues.type}
                onChange={handleSelectChange}
                error={touched.type && !!errors.type}
                errorMessage={getErrorMessage(errors.type)}
                options={[
                  { value: "LECTURE", label: "Palestra" },
                  { value: "WORKSHOP", label: "Workshop" },
                ]}
                block
              />
            </Wrapper>

            <Wrapper>
              <InputFormField
                label="Número de Assentos"
                name="numberOfSeats"
                type="number"
                value={inputsValues.numberOfSeats}
                onChange={handleInputChange}
                error={touched.numberOfSeats && !!errors.numberOfSeats}
                mensageError={getErrorMessage(errors.numberOfSeats)}
                block
                placeholder="Deixe em branco se não houver limite"
              />
            </Wrapper>
            {editingSession?.numberOfSeats &&
            editingSession?.numberOfSeats > 0 ? (
              <Wrapper>
                <InputFormField
                  label="Assentos Disponíveis"
                  name="availableSeats"
                  type="number"
                  value={inputsValues.availableSeats}
                  disabled={true}
                  block
                />
              </Wrapper>
            ) : null}
          </FormSection>
        </Card>

        <Card title="Trilhas e Palestrantes" typography="subtitle-medium">
          <FormSection>
            <Wrapper>
              <div style={{ marginBottom: "1rem" }}>
                {loadingOptions ? (
                  <div>Carregando trilhas...</div>
                ) : (
                  <MultiSelect
                    name="tracks"
                    label="Trilhas Selecionadas"
                    options={trackOptions}
                    value={inputsValues.trackIds}
                    onChange={handleTracksArrayChange}
                    block={true}
                    search={true}
                    error={false}
                    errorMessage=""
                  />
                )}
              </div>
            </Wrapper>

            <Wrapper>
              <div>
                {loadingOptions ? (
                  <div>Carregando palestrantes...</div>
                ) : (
                  <MultiSelect
                    name="speakers"
                    label="Palestrantes Selecionados"
                    options={speakerOptions}
                    value={inputsValues.speakerIds}
                    onChange={handleSpeakersArrayChange}
                    block={true}
                    search={true}
                    error={false}
                    errorMessage=""
                  />
                )}
              </div>
            </Wrapper>

            {(!isEditing ||
              (isEditing &&
                !hasExistingConversation &&
                !checkingConversation)) && (
              <Wrapper>
                <div style={{ marginTop: "1rem" }}>
                  <CheckBox
                    label="Criar canal de conversa para sessão"
                    name="createPublicChannel"
                    value="createPublicChannel"
                    checked={inputsValues.createPublicChannel}
                    onChange={(event) => {
                      handleCreatePublicChannelChange(event.checked);
                    }}
                  />
                </div>
              </Wrapper>
            )}

            {isEditing && checkingConversation && (
              <Wrapper>
                <div style={{ marginTop: "1rem", color: "#666" }}>
                  Verificando canal existente...
                </div>
              </Wrapper>
            )}

            {isEditing && hasExistingConversation && !checkingConversation && (
              <Wrapper>
                <div
                  style={{
                    marginTop: "1rem",
                    color: "#28a745",
                    fontSize: "14px",
                  }}
                >
                  ✓ Canal público já existe para esta sessão
                </div>
              </Wrapper>
            )}

            {/* Channel fields - show when checkbox is checked OR when editing with existing conversation */}
            {(inputsValues.createPublicChannel ||
              (isEditing &&
                hasExistingConversation &&
                !checkingConversation)) && (
              <>
                <Wrapper>
                  <InputFormField
                    label="Nome do Canal *"
                    name="channelName"
                    value={inputsValues.channelName}
                    onChange={handleInputChange}
                    error={touched.channelName && !!errors.channelName}
                    mensageError={getErrorMessage(errors.channelName)}
                    block
                  />
                </Wrapper>

                <Wrapper>
                  <TextFormField
                    label="Descrição do Canal"
                    name="channelDescription"
                    value={inputsValues.channelDescription}
                    onChange={handleInputChange}
                    error={
                      touched.channelDescription && !!errors.channelDescription
                    }
                    mensageError={getErrorMessage(errors.channelDescription)}
                    block
                    rows={3}
                  />
                </Wrapper>

                <Wrapper>
                  <div style={{ marginTop: "1rem" }}>
                    <CheckBox
                      label="Permitir visualização pública"
                      name="allowPublicView"
                      value="allowPublicView"
                      checked={inputsValues.allowPublicView}
                      onChange={(event) => {
                        handleAllowPublicViewChange(event.checked);
                      }}
                    />
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#6c757d",
                        marginTop: "0.5rem",
                        marginLeft: "1.75rem",
                      }}
                    >
                      Usuários autenticados que não estão registrados no evento
                      poderão visualizar esta conversa em modo somente leitura
                    </div>
                  </div>
                </Wrapper>
              </>
            )}
          </FormSection>
        </Card>

        <Card title="Conteúdo" typography="subtitle-medium">
          <FormSection>
            <Wrapper>
              <InputFormField
                label="Link do Vídeo"
                name="videoLink"
                type="url"
                value={inputsValues.videoLink || ""}
                onChange={handleInputChange}
                error={touched.videoLink && !!errors.videoLink}
                mensageError={getErrorMessage(errors.videoLink)}
                block
                placeholder="https://exemplo.com/video"
                disabled={!isEditing}
              />
            </Wrapper>

            <Wrapper>
              <InputMultipleFiles
                files={uploadedFiles}
                onAddFiles={handleAddFiles}
                onRemoveFile={handleRemoveFile}
                maxFiles={1}
                accept=".pdf,.ppt,.pptx,.doc,.docx"
              />
            </Wrapper>
          </FormSection>
        </Card>

        <Card title="Teste de conhecimento" typography="subtitle-medium">
          <FormSection>
            <QuestionsListContainer>
              <label>Upload de Transcrição</label>
              <InputTranscriptFile
                onFileSelect={handleFileUpload}
                selectedFile={transcriptFile}
                error={false}
                errorMessage=""
              />
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#6c757d",
                }}
              >
                Formatos suportados: TXT, PDF, DOC, DOCX
              </div>
              <TextFormField
                label="Transcrição da Sessão"
                name="transcript"
                value={inputsValues.transcript}
                onChange={handleInputChange}
                error={false}
                mensageError=""
                block
                rows={16}
                placeholder="Cole a transcrição aqui ou faça upload de um arquivo acima..."
              />
              <div
                style={{
                  fontSize: "0.875rem",
                  color:
                    inputsValues.transcript.length < 50 ? "#dc3545" : "#6c757d",
                  marginTop: "0.5rem",
                }}
              >
                {inputsValues.transcript.length}/50 caracteres mínimos
                {inputsValues.transcript.length < 50 && (
                  <span style={{ marginLeft: "0.5rem" }}>
                    (Necessário mais {50 - inputsValues.transcript.length}{" "}
                    caracteres)
                  </span>
                )}
              </div>

              {/* Questions List - Show when questionnaire exists */}
              {editingSession?.questionnaire && (
                <QuestionsList
                  questionnaire={editingSession.questionnaire}
                  onQuestionnaireUpdate={onQuestionnaireUpdate}
                  onEditClick={handleOpenQuestionnaireModal}
                />
              )}
              <QuestionActions>
                {!editingSession?.questionnaire?.isPublished && (
                  <Button
                    label="Gerar Questionário"
                    typeBtn="blue"
                    type="button"
                    load={generatingQuestionnaire}
                    disabled={
                      generatingQuestionnaire ||
                      !inputsValues.transcript.trim() ||
                      inputsValues.transcript.length < 50
                    }
                    onClick={handleGenerateQuestionnaire}
                  />
                )}
                {editingSession?.questionnaire && (
                  <Button
                    label={
                      editingSession.questionnaire.isPublished
                        ? "Questionário Publicado"
                        : "Publicar Questionário"
                    }
                    typeBtn="success"
                    type="button"
                    load={publishingQuestionnaire}
                    disabled={
                      publishingQuestionnaire ||
                      editingSession.questionnaire.isPublished
                    }
                    onClick={handlePublishQuestionnaire}
                  />
                )}
              </QuestionActions>
            </QuestionsListContainer>
          </FormSection>
        </Card>

        <FormActions>
          {isEditing ? (
            <Button
              label="Excluir Sessão"
              typeBtn="danger"
              icon={iconWhiteTrash}
              onClick={handleDeleteSession}
              type="button"
            />
          ) : (
            <div />
          )}
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              label="Cancelar"
              typeBtn="outline"
              onClick={() => push(`/events/${eventId}?tab=Sessões`)}
              type="button"
            />
            <Button
              label={isEditing ? "Salvar Alterações" : "Criar Sessão"}
              typeBtn="blue"
              type="submit"
              load={isLoading}
              disabled={isLoading}
            />
          </div>
        </FormActions>
      </form>

      {editingSession?.questionnaire && (
        <Modal
          open={isQuestionnaireModalVisible}
          onClose={handleCloseQuestionnaireModal}
        >
          <QuestionnaireUpdateForm
            questionnaire={editingSession.questionnaire}
            onSuccess={onQuestionnaireUpdate}
            onCancel={handleCloseQuestionnaireModal}
          />
        </Modal>
      )}
    </Container>
  );
};
